import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import {
  getContentDetails,
  getContentModule,
  saveContentHistory,
  submitContentFeedback,
  toggleContentFavorite,
  toggleContentLike,
} from "../../services/portalAluno/contentDetails/contentDetailsService";
import type {
  ContentDetailsActionType,
  ContentDetailsEntityType,
} from "../../@types/portalAluno";
import {
  invalidatePortalQueriesExceptHome,
  refreshHomeAfterContentCompletion,
} from "../../services/portalAluno/home/homeCache";
import type { PortalContentKind } from "../../@types/portalAluno";
import type { ApplicationState } from "../../store";

export const portalContentDetailsQueryKey = (
  idUsuario?: number,
  sourceType?: PortalContentKind,
  id?: number
) => ["portal-aluno", "detalhes", idUsuario, sourceType, id];

const useContentDetails = (id?: number, sourceType?: PortalContentKind) => {
  const auth = useSelector((state: ApplicationState) => state.auth.data);
  const idUsuario = auth?.idUsuario;
  const queryClient = useQueryClient();

  const query = useQuery({
    enabled: Boolean(idUsuario && sourceType && id && id > 0),
    queryKey: portalContentDetailsQueryKey(idUsuario, sourceType, id),
    queryFn: () =>
      getContentDetails(id as number, sourceType as PortalContentKind),
  });

  const invalidatePortalContent = () =>
    invalidatePortalQueriesExceptHome(queryClient, idUsuario);

  const favoriteMutation = useMutation({
    mutationFn: ({
      contentId,
      type,
    }: {
      contentId: number;
      type: ContentDetailsActionType;
    }) =>
      toggleContentFavorite(contentId, type),
    onSuccess: invalidatePortalContent,
  });

  const likeMutation = useMutation({
    mutationFn: ({
      contentId,
      type,
    }: {
      contentId: number;
      type: ContentDetailsActionType;
    }) =>
      toggleContentLike(contentId, type),
    onSuccess: invalidatePortalContent,
  });

  const historyMutation = useMutation({
    mutationFn: (idConteudo: number) => saveContentHistory(idConteudo),
    onSuccess: async () => {
      await Promise.all([
        invalidatePortalContent().catch(() => undefined),
        refreshHomeAfterContentCompletion(queryClient, idUsuario).catch(
          () => undefined
        ),
      ]);
    },
  });

  const moduleMutation = useMutation({
    mutationFn: (idConteudo: number) => getContentModule(idConteudo),
  });

  const feedbackMutation = useMutation({
    mutationFn: ({
      contentId,
      description,
      entityType,
      rating,
    }: {
      contentId: number;
      description: string;
      entityType: ContentDetailsEntityType;
      rating: number;
    }) =>
      submitContentFeedback(contentId, entityType, rating, description),
    onSuccess: invalidatePortalContent,
  });

  return {
    ...query,
    isActionPending:
      favoriteMutation.isLoading ||
      likeMutation.isLoading ||
      feedbackMutation.isLoading,
    loadModule: moduleMutation.mutateAsync,
    markHistory: historyMutation.mutateAsync,
    submitFeedback: feedbackMutation.mutateAsync,
    toggleFavorite: favoriteMutation.mutateAsync,
    toggleLike: likeMutation.mutateAsync,
  };
};

export default useContentDetails;
